import * as m from '@staiter/i18n';
// S-FIX-6 fix: list responses use SettlementListItem (omits `lines`, adds counterparty_name).
import type { SettlementListItem } from '@staiter/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Plus, Scale } from 'lucide-react';
import { useMemo } from 'react';
import { EmptyState, PageHeader, Table, type TableColumn } from '~/components/data';
import { PeriodCloseBanner } from '~/components/domain';
import { Button } from '~/components/primitives';
import { fetchSettlements } from '~/lib/api/settlements';
import { formatDate, formatMoney } from '~/lib/format';
import { canDo, useUserScope } from '~/lib/rbac/use-user-scope';
import shared from './_auth._shared.module.css';

/**
 * S14-FE.9 — Settlements list page.
 */

export const Route = createFileRoute('/_auth/settlements/')({ component: SettlementsPage });

const STATE_LABELS: Record<string, () => string> = {
  draft: m.settlement_state_draft,
  posted: m.settlement_state_posted,
  cancelled: m.settlement_state_cancelled,
};

function SettlementsPage(): JSX.Element {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['settlements'],
    queryFn: () => fetchSettlements({ limit: 200 }),
  });
  const rows = query.data?.items ?? [];
  // access-control (F9) — колонка компании только в holding-агрегате.
  const entityNames =
    (query.data as { entities?: Record<string, { name: string }> } | undefined)?.entities ?? {};
  const showCompanyCol = new Set(rows.map((r) => r.entity_id)).size > 1;

  const columns: TableColumn<SettlementListItem>[] = useMemo(
    () => [
      ...(showCompanyCol
        ? [
            {
              id: 'company',
              header: m.cp_field_company(),
              accessor: (r: SettlementListItem) => entityNames[r.entity_id]?.name ?? '—',
              width: '150px',
            } satisfies TableColumn<SettlementListItem>,
          ]
        : []),
      {
        id: 'doc',
        header: m.settlement_column_doc_number(),
        accessor: (r) => (
          <Link to="/settlements/$id" params={{ id: r.id }} className={shared.accentLink}>
            {r.document_number}
          </Link>
        ),
        mono: true,
        sticky: true,
      },
      {
        id: 'date',
        header: m.settlement_column_date(),
        accessor: (r) => formatDate(r.settlement_date),
        width: '120px',
      },
      {
        id: 'net',
        header: m.settlement_column_net_amount(),
        accessor: (r) => formatMoney(r.net_amount),
        align: 'right',
        mono: true,
      },
      {
        id: 'state',
        header: m.settlement_column_state(),
        accessor: (r) => (STATE_LABELS[r.state] ?? (() => r.state))(),
        width: '120px',
      },
    ],
    [showCompanyCol, entityNames],
  );

  const scope = useUserScope();
  const canCreate = canDo(scope, 'settlement', 'create');
  return (
    <div>
      <PageHeader
        title={m.phase2_settlements_title()}
        actions={
          // F38 (WHS-F38): создание — только роли с правом `settlement:create`.
          canCreate ? (
            <Button
              variant="primary"
              leadingIcon={<Plus size={16} />}
              onClick={() => void navigate({ to: '/settlements/new' })}
            >
              {m.settlement_button_new()}
            </Button>
          ) : undefined
        }
      />
      <PeriodCloseBanner scope="settlement" />
      {rows.length === 0 && !query.isLoading ? (
        <EmptyState title={m.settlement_empty_title()} icon={<Scale size={32} />} />
      ) : (
        <Table<SettlementListItem>
          data={rows}
          columns={columns}
          rowKey={(r) => r.id}
          ariaLabel={m.phase2_settlements_title()}
          loading={query.isLoading}
          emptyTitle={m.settlement_empty_title()}
          stickyFirstColumn
        />
      )}
    </div>
  );
}
